import { Icon, IconsEnum } from 'components/Icons';
import { setAccessData, setAntiFraudCode, setTriggerForm } from 'features/signup/signup';
import { RootState, useAppDispatch } from 'lib/centralStore';
import Persist, { StorageKind } from 'lib/persist';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { capitalize, gtag } from 'utility/functions';
import { PathName, getRouteByKey } from 'utility/routes';
import { AccountData, AccountDataLabel, AccountDataProps } from '../accountData/AccountData';
import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordInput } from 'components/inputs/passwordInput';
import { SelectInput } from 'components/inputs/selectInput';
import { TextInput } from 'components/inputs/textInput';
import styles from 'components/page-content/registrati/registrati.module.scss';
import { RichText } from 'components/richText';
import { useLazyCheckUsernameQuery } from 'features/api/userSlice';
import {
  PASSWORD_VALIDATION_REGEX,
  REGISTRATION_STEP_KEY,
  SPECIAL_CHARACTERS_ALLOWED,
  USERNAME_VALIDATION_REGEX,
} from 'features/signup/constants';
import { useDebounce } from 'hooks';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { ButtonRegistrationWrapper } from '../buttonRegistrationWrapper';
import { PasswordCheckList } from '../passwordCheckList';
import AccessDataStyles from './AccessData.module.scss';

const localStorage = Persist(StorageKind.local);
type QueryParam = string | undefined;

export type LabelAccessData = {
  titleAccessData: string;
  descriptionAccessData: string;
  titleProtectAccount: string;
  descriptionProtectAccount: string;
  username: string;
  placeholderUsername: string;
  password: string;
  repeatPassword: string;
  placeholderPassword: string;
  secretQuestion: Array<{ value: string; label: string }>;
  question: string;
  answer: string;
  placeholderAnswer: string;
  passwordMismatch: string;
  usernameNotValid: string;
  usernameExist: string;
  genericError: string;
  labelUsernameRules: string;
};

export type AccessDataProps = {
  next: Dispatch<SetStateAction<boolean>>;
  back?: Dispatch<SetStateAction<boolean>>;
  labels: LabelAccessData;
  buttonData?: {
    seeYourData: string;
    hideYourData: string;
  };
  accountDataLabel?: AccountDataLabel;
  isYouDataVisible?: boolean;
};

const ALLOWED_FROM_PASSWORD = ['paymatST'];
const MIN_USERNAME_LENGTH = 8;
const MAX_USERNAME_LENGTH = 20;

export const AccessData: React.FC<AccessDataProps> = ({
  next,
  back,
  labels,
  buttonData,
  accountDataLabel,
  isYouDataVisible,
}) => {
  const [showYourData, setShowYourData] = useState<boolean>(false);
  const state = useSelector((store: RootState) => store.signup);
  const [isQuestionStepVisible, setIsQuestionStepVisible] = useState(!!state.secretAnswer && !isYouDataVisible);

  const [trigger] = useLazyCheckUsernameQuery();
  const { asPath, query } = useRouter();
  const registrationUrl = useMemo(() => getRouteByKey(PathName.registrationUrl), []);

  const { passwordFromQuery, fromPwd } = useMemo(
    () => ({
      fromPwd: query['from_pwd'] as QueryParam,
      passwordFromQuery: query['pwdpm'] as QueryParam,
    }),
    [query]
  );
  const hasPasswordFromQuery = useMemo(
    () => ALLOWED_FROM_PASSWORD.includes(fromPwd ?? '') && !!passwordFromQuery,
    [fromPwd, passwordFromQuery]
  );

  const [
    labelContinua,
    labelRuleCharacters,
    labelRuleAtLeastOneNumber,
    labelRuleUpperLowerCase,
    labelRuleSpecialCharacter,
    labelGreeting,
  ] = useGetLabelsByKeys([
    'continua',
    'regola-8-caratteri',
    'almeno-un-numero',
    'caratteri-maiuscoli-minuscoli',
    'regola-caratteri-speciali',
    'greeting',
  ]);

  const dispatch = useAppDispatch();
  const usernameSchema = useMemo(
    () =>
      z
        .string()
        .min(MIN_USERNAME_LENGTH)
        .max(MAX_USERNAME_LENGTH)
        .regex(USERNAME_VALIDATION_REGEX, labels.usernameNotValid)
        .transform((t) => t?.trim()),
    [labels]
  );

  const passwordValidation = useMemo(() => z.string().regex(PASSWORD_VALIDATION_REGEX), []);
  const accessInfoSchema = useMemo(
    () =>
      z
        .object({
          username: usernameSchema,
          password: hasPasswordFromQuery ? z.string().optional() : passwordValidation,
          repeatPassword: hasPasswordFromQuery ? z.string().optional() : z.string(),
        })
        .refine((schema) => schema.password === schema.repeatPassword, {
          message: labels.passwordMismatch,
          path: ['repeatPassword'],
        }),
    [hasPasswordFromQuery, labels.passwordMismatch, passwordValidation, usernameSchema]
  );

  const protectInformationSchema = useMemo(
    () =>
      z.object({
        question: z.string().min(1),
        answer: z.string().min(1),
      }),
    []
  );

  type AccessInfoValidation = z.infer<typeof accessInfoSchema>;
  type ProtectInformationValidation = z.infer<typeof protectInformationSchema>;

  const { control, handleSubmit, setError, watch, clearErrors, getValues, formState } = useForm<AccessInfoValidation>({
    resolver: zodResolver(accessInfoSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      password: state.password,
      repeatPassword: state.password,
      username: state.username,
    },
  });

  const checkUsername = useCallback(
    async (username: string) => {
      try {
        const { success } = await trigger({
          username: username,
        }).unwrap();

        if (!success) {
          setError('username', {
            message: labels.usernameExist,
          });
          return false;
        }

        clearErrors('username');
        return true;
      } catch (error) {
        if (error?.data?.response?.descrizione) {
          setError('username', {
            message: error.data.response.descrizione,
          });
        } else {
          setError('username', {
            message: labels.genericError,
          });
        }
        return false;
      }
    },
    [clearErrors, setError, trigger, labels]
  );

  const {
    control: controlSecondForm,
    handleSubmit: handleSecondSubmit,
    formState: formStateSecondForm,
  } = useForm<ProtectInformationValidation>({
    resolver: zodResolver(protectInformationSchema),
    // mode: 'onChange',
    // reValidateMode: 'onChange',
    defaultValues: {
      answer: state.secretAnswer,
      question: state.secretQuestion,
    },
  });

  const { debouncedValue: debouncedUsername } = useDebounce(watch('username'), 1500);

  const firstStepHandler: SubmitHandler<AccessInfoValidation> = useCallback(async () => {
    const usernameCheck = await checkUsername(debouncedUsername);
    if (usernameCheck && Object.keys(formState.errors).length === 0) {
      setIsQuestionStepVisible(true);
    }
  }, [checkUsername, debouncedUsername, formState.errors]);

  useEffect(() => {
    try {
      const isValid = debouncedUsername && usernameSchema.parse(debouncedUsername);
      if (isValid) {
        checkUsername(debouncedUsername!);
      }
    } catch (error) {}
  }, [debouncedUsername, checkUsername, usernameSchema]);

  useEffect(() => {
    if (asPath.includes(registrationUrl)) {
      const step = localStorage.getItem(REGISTRATION_STEP_KEY);
      if (isQuestionStepVisible) {
        if (step !== 'access-data-secret-question') {
          localStorage.setItem(REGISTRATION_STEP_KEY, 'access-data-secret-question');
          gtag(
            {
              section: 'RegistrationStepAccessDataSecretQuestion',
              detail: 'RegistrationStepAccessDataSecretQuestion',
              event: 'RegistrationStepAccessDataSecretQuestion',
            },
            true
          );
        }
      } else if (step !== 'access-data-username-password') {
        localStorage.setItem(REGISTRATION_STEP_KEY, 'access-data-username-password');
        gtag(
          {
            section: 'RegistrationStepAccessDataUsernamePassword',
            detail: 'RegistrationStepAccessDataUsernamePassword',
            event: 'RegistrationStepAccessDataUsernamePassword',
          },
          true
        );
      }
    }
  }, [registrationUrl, asPath, isQuestionStepVisible]);

  useEffect(() => {
    dispatch(setTriggerForm(false));
  }, [dispatch]);

  const submitWholeForm: SubmitHandler<ProtectInformationValidation> = useCallback(
    ({ answer, question }) => {
      // @ts-ignore
      if (window.IGLOO && window.IGLOO.getBlackbox) {
        // @ts-ignore
        dispatch(setAntiFraudCode(window.IGLOO.getBlackbox().blackbox));
      }
      dispatch(
        setAccessData({
          username: getValues('username'),
          password: hasPasswordFromQuery ? passwordFromQuery! : getValues('password')!,
          secretQuestion: question,
          secretAnswer: answer,
        })
      );
      next(true);
    },
    [dispatch, getValues, hasPasswordFromQuery, next, passwordFromQuery]
  );

  const handleSeeYourData = useCallback(() => {
    setShowYourData((prevState) => !prevState);
  }, []);

  const datas: AccountDataProps = useMemo(
    () => ({
      name: state.name,
      surname: state.surname,
      email: state.email,
      telephone: state.telephone,
      fiscalCode: state.fiscalCode,
      province: state.provinceOfBirth,
      city: state.city,
      address: state.address,
      zipCode: state.cap,
      labelEmail: accountDataLabel?.labelEmail || '',
      labelTelephone: accountDataLabel?.labelTelephone || '',
      labelName: accountDataLabel?.labelName || '',
      labelsurname: accountDataLabel?.labelSurname || '',
      labelFiscalCode: accountDataLabel?.labelFiscalCode || '',
      labelProvince: accountDataLabel?.labelProvince || '',
      labelCity: accountDataLabel?.labelCity || '',
      labelAddress: accountDataLabel?.labelAddress || '',
      labelZipCode: accountDataLabel?.labelZipCode || '',
    }),
    [accountDataLabel, state]
  );

  return (
    <div className={styles.formContainer}>
      {isQuestionStepVisible ? (
        <React.Fragment>
          <h2 className={styles.title}>{labels.titleProtectAccount}</h2>
          <RichText containerClassName={styles.subTitle}>{labels.descriptionProtectAccount}</RichText>
          <form onSubmit={handleSecondSubmit(submitWholeForm)} className={styles.form}>
            <SelectInput
              control={controlSecondForm}
              name="question"
              options={labels.secretQuestion}
              label={labels.question}
              hasMarginBottom={true}
            />
            <TextInput
              label={labels.answer}
              control={controlSecondForm}
              placeholder={labels.placeholderAnswer}
              name="answer"
            />
            <ButtonRegistrationWrapper
              handleGoBack={() => setIsQuestionStepVisible(false)}
              isNextDisabled={!formStateSecondForm.isValid}
            />
          </form>
        </React.Fragment>
      ) : (
        <React.Fragment>
          {buttonData ? (
            <div className={AccessDataStyles.titleContainer}>
              <h2 className={AccessDataStyles.title}>
                {labelGreeting} <span className={AccessDataStyles.userName}>{capitalize(datas.name)}</span>
              </h2>
              <button className={AccessDataStyles.button} onClick={() => handleSeeYourData()}>
                <span className={AccessDataStyles.span}>
                  {showYourData ? buttonData.hideYourData : buttonData.seeYourData}
                </span>
                <Icon
                  iconId={IconsEnum.CHEVRON_DOWN}
                  color="var(--color-on-bg-primary)"
                  className={`${AccessDataStyles.chewronIcon} ${showYourData && AccessDataStyles.chewronUp}`}
                />
              </button>
            </div>
          ) : (
            <h2 className={styles.title}>{labels.titleAccessData}</h2>
          )}
          <RichText containerClassName={styles.subTitle}>{labels.descriptionAccessData}</RichText>

          {showYourData && <AccountData {...datas}></AccountData>}
          <form onSubmit={handleSubmit(firstStepHandler)} className={styles.form}>
            <TextInput
              label={labels.username}
              placeholder={labels.placeholderUsername}
              control={control}
              name="username"
              hasMarginBottom={true}
              tolltipString={labels.labelUsernameRules}
              onChange={(element) => {
                try {
                  const isValid = element.target.value.length && usernameSchema.parse(element.target.value);
                  if (isValid) {
                    setTimeout(() => {
                      setError('username', {
                        type: 'loading',
                      });
                    }, 0);
                  }
                } catch (error) {}
              }}
            />

            {!hasPasswordFromQuery && (
              <>
                <PasswordInput
                  label={labels.password}
                  control={control}
                  name="password"
                  placeholder={labels.placeholderPassword}
                  isErrorHidden
                  autoComplete="new-password"
                  hasMarginBottom={true}
                  onChange={(e) => {
                    if (!e.target.value)
                      setError('password', {
                        type: 'required',
                      });
                  }}
                />
                <PasswordCheckList
                  rules={[
                    {
                      label: labelRuleCharacters,
                      pattern: (value) => value.length >= MIN_USERNAME_LENGTH && value.length <= MAX_USERNAME_LENGTH,
                    },
                    {
                      label: labelRuleAtLeastOneNumber,
                      pattern: /(?=.*[0-9])/g,
                    },
                    {
                      label: labelRuleUpperLowerCase,
                      pattern: /(?=.*[a-z])(?=.*[A-Z])/g,
                    },
                    {
                      label: labelRuleSpecialCharacter,
                      pattern: SPECIAL_CHARACTERS_ALLOWED,
                    },
                  ]}
                  value={watch()?.password ?? ''}
                  onCheckState={(state: boolean) => {
                    if (!state) {
                      setError('password', {
                        types: {
                          custom: 'checklist',
                        },
                      });
                    }
                  }}
                />
                <PasswordInput
                  label={labels.repeatPassword}
                  placeholder={labels.repeatPassword}
                  control={control}
                  name="repeatPassword"
                />
              </>
            )}
            {back ? (
              <ButtonRegistrationWrapper handleGoBack={back!} isNextDisabled={!formState.isValid} />
            ) : (
              // disabled={!!Object.keys(formState.errors).length}
              <button className={styles.singleButton} type="submit">
                {labelContinua}
              </button>
            )}
          </form>
        </React.Fragment>
      )}
    </div>
  );
};
